import type { RowData } from "@tanstack/react-table";
import type { DataGridCell, DataGridEditor } from "../columns/types";
import {
    type EditContext,
    type EditorCommit,
    cancelEdit,
    commitEdit,
} from "./editActions";
import { toEditorText, toEditorValue } from "./editorValues";
import { DateEditor } from "./editors/DateEditor";
import type { EditorProps } from "./editors/editorProps";
import { MoneyEditor } from "./editors/MoneyEditor";
import { NumberEditor } from "./editors/NumberEditor";
import { PickerEditor } from "./editors/PickerEditor";
import { TextEditor } from "./editors/TextEditor";

interface CellEditorProps<TData extends RowData> {
    cell: DataGridCell<TData>;
    editor: DataGridEditor;
    /** The typed key that replaces the value; `null` edits the value in place. */
    seed: string | null;
    header: string;
    context: EditContext<TData>;
}

/** The open editor of the active cell, chosen by the column's `editor`. */
export const CellEditor = <TData extends RowData>({
    cell,
    editor,
    seed,
    header,
    context,
}: CellEditorProps<TData>) => {
    const onCommit = (input: EditorCommit) => commitEdit(context, input);
    const onCancel = () => cancelEdit(context);
    const value = cell.getValue();

    if (typeof editor !== "string") {
        const current = toEditorValue({ editor, value });
        return (
            <PickerEditor
                editor={editor}
                value={typeof current === "string" ? current : null}
                seed={seed}
                onCommit={onCommit}
                onCancel={onCancel}
            />
        );
    }
    const props: EditorProps = {
        initialText:
            seed ??
            toEditorText({ editor, value: toEditorValue({ editor, value }) }),
        isReplacing: seed !== null,
        ariaLabel: header,
        onCommit,
        onCancel,
    };
    switch (editor) {
        case "number":
            return <NumberEditor {...props} />;
        case "money":
            return <MoneyEditor {...props} />;
        case "date":
            return <DateEditor {...props} />;
        case "text":
            return <TextEditor {...props} />;
    }
};
